import { effectiveBoundary } from "@app/core";
import { getServices } from "../../platform.js";
import { partitionAndDelete } from "./helpers.js";
import type { SeriesGet, SeriesSet } from "./types.js";

/**
 * Cap a series at `occurrenceIso` with no new segment: sets `rule.until`,
 * hard-deletes superseded thisandfuture overrides, and removes single overrides
 * past the cap (they can never be reached again). Used by "delete this and
 * following" so `rule.until` stays accurate and consistent with the split path.
 */
export async function capSeriesAt(
  set: SeriesSet,
  get: SeriesGet,
  sourceId: string,
  occurrenceIso: string,
): Promise<void> {
  const source = get().tasks.find((t) => t.id === sourceId);
  if (!source || !source.recurrence_id) return;

  const { recurrence } = getServices();
  const rule = get().rules[source.recurrence_id]
    ?? await recurrence.findById(source.recurrence_id);
  if (!rule) return;

  const overrides = get().overrides[rule.id] ?? [];
  const now = new Date().toISOString();

  const { remaining } = await partitionAndDelete(
    overrides,
    (o) =>
      (o.range === "thisandfuture" && effectiveBoundary(o) >= occurrenceIso) ||
      (o.range === "single" && o.recurrence_id_ref >= occurrenceIso),
  );

  const cappedRule = { ...rule, until: occurrenceIso, updated_at: now };
  await recurrence.update(cappedRule);

  set({
    rules: { ...get().rules, [rule.id]: cappedRule },
    overrides: { ...get().overrides, [rule.id]: remaining },
  });
}
